import { Injectable } from "@angular/core";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";

import { ModalMessages } from "../types";
import { UnsubscribeOnDestroy } from "../classes";

export class ModalButton {
    text: string;
    /** Host-only classes (e.g. pull-right). Do not use Bootstrap `btn` / `btn-*`. */
    cssClass?: string[];
    result: any;
    /** PrimeNG `p-button` severity. */
    severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'help' | 'danger' | 'contrast';
    outlined?: boolean;
}

export class ModalOptions {
    content: string;
    type?: string;
    buttons?: ModalButton[];
    header?: string;
    autodismiss?: boolean | number = false;
}

@Injectable({
    "providedIn": "root"
})
export class ModalService extends UnsubscribeOnDestroy {
    modalObs: Subject<ModalOptions>;
    resultObs: Subject<any>;
    queue: [ModalOptions, Subject<any>][];
    private sub: Subscription;

    constructor() {
        super();
        this.modalObs = new Subject<ModalOptions>();
        this.resultObs = new Subject<any>();
        this.queue = [];
    }

    showModal(options: ModalOptions): Observable<any> {
        const result = new Subject<any>();
        this.queue.push([options, result]);

        if (this.queue.length == 1) {
            this.startModal(options, result);
        }
        return result;
    }

    showAttentionModal(content: string): Observable<any> {
        const options = new ModalOptions();
        options.content = content;
        options.type = "confirmation";
        options.header = "Attention";
        options.buttons = [
            { text: "No", result: false, severity: "secondary", outlined: true },
            { text: "Yes", result: true, severity: "primary" },
        ];

        return this.showModal(options);
    }

    showWarningModal(content: string): Observable<any> {
        const options = new ModalOptions();
        options.content = content;
        options.type = "confirmation";
        options.header = "Warning";
        options.buttons = [
            { text: "No", result: false, severity: "secondary", outlined: true },
            { text: "Yes", result: true, severity: "primary" },
        ];

        return this.showModal(options);
    }

    showSuccessModal(content: string = ""): Observable<any> {
        const options = new ModalOptions();
        options.content = content.length > 0 ? content : ModalMessages.Success;
        options.type = "success";
        options.autodismiss = true;
        return this.showModal(options);
    }

    showErrorModal(errorMessage: string = null): Observable<any> {
        const options = new ModalOptions();

        if (errorMessage) {
            options.content = errorMessage;
        } else {
            options.content = ModalMessages.Error;
        }

        options.type = "error";
        options.autodismiss = true;
        return this.showModal(options);
    }

    showUnsavedModal(): Observable<any> {
        const options = new ModalOptions();
        options.content = ModalMessages.Unsaved;
        options.type = "confirmation";
        options.header = "Warning";
        options.buttons = [
            { text: "No", result: false, severity: "secondary", outlined: true },
            { text: "Yes", result: true, severity: "primary" },
        ];
        return this.showModal(options);
    }

    private startModal(options: ModalOptions, result: Subject<any>): void {
        this.modalObs.next(options);

        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.sub = this.resultObs
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((res) => {
                this.queue.shift();
                result.next(res);

                if (this.queue.length > 0) {
                    const [nextOptions, nextResult] = this.queue[0];
                    this.startModal(nextOptions, nextResult);
                }
            });
    }
}
